import { useRef, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { X } from 'lucide-react'
import { api } from '../../../lib/api'
import { Button } from '../../ui/Button'
import { Modal } from '../../ui/Modal'
import { eroare } from './comun'
import { useAuthStore } from '../../../stores/auth'
import { CautaAdaugaPartener, type Partener } from '../../parteneri/CautaAdaugaPartener'

/** Beneficiarul unei sponsorizări nu e neapărat client: implicit „partener", fără agent. */
const GRUPURI_BENEFICIAR = [
  { value: 'partener', label: 'Partener (nu e client)' },
  { value: 'B2B', label: 'B2B' },
  { value: 'B2C', label: 'B2C' },
]
import type { Cerere } from './tipuriNecesar'
import { BlocLivrare, livrareDin, type Livrare } from './BlocLivrare'

/**
 * Formularul cererii de marfă.
 *
 * Prima versiune punea toate produsele într-o listă lungă, cu o căsuță de cantitate pe fiecare
 * rând. Ce alegeai se pierdea printre celelalte: „e foarte greu de urmărit, nici nu știu ce am
 * adăugat. Am ajuns să salvez după fiecare adăugare, de teamă să nu pierd datele" (Alexandru,
 * 22.09.2026). Frica de pierdere venea din altceva — fereastra se închidea la un clic alături,
 * reparat separat — dar urmărirea e problema ecranului.
 *
 * Acum sunt două zone: SUS ce ai cerut, mereu la vedere, cu cantitățile editabile și cu buton de
 * scoatere; JOS catalogul din care alegi, fără produsele deja adăugate.
 */

interface ProdusCatalog {
  id: number
  sku: string
  name: string
  stock: string | null
}

interface Actiune {
  id: number
  denumire: string
}

interface LinieAleasa {
  productId: number
  sku: string
  denumire: string
  cantitate: number
}

export function FormularCerere({
  campanieId,
  initial,
  onClose,
  onGata,
  teren = false,
  cuTrimitere = false,
}: {
  campanieId: number
  initial?: Cerere | null
  onClose: () => void
  onGata: () => void
  /**
   * Cererea „pentru mine" (Necesar teren, 26.10.2026): marfa ajunge la cel care o cere, deci fără
   * colaborator, partener sau acțiune — iar „pentru ce" devine obligatoriu: e singurul loc care
   * spune unde ajunge marfa.
   */
  teren?: boolean
  /** Butonul „Trimite cererea" lângă „Salvează ciorna" — din ecranul „Cereri de marfă”. */
  cuTrimitere?: boolean
}) {
  const permisiuni = useAuthStore((s) => s.permissions)
  // Lista colaboratorilor cere dreptul pe influenceri; fără el, opțiunea ar da o listă goală.
  const vedeColaboratori = permisiuni.includes('marketing.influencers:read')
  const [motiv, setMotiv] = useState(initial?.motiv ?? '')
  const [actiuneId, setActiuneId] = useState<number | null>(initial?.actiuneId ?? null)
  /*
    CUI ii pleaca marfa.

    `activitate` — calea obisnuita: marfa se transfera cuiva din firma, care o distribuie apoi de
    la stand sau la colectarea datelor.

    `colaborator` — marfa pleaca DIRECT catre un influencer, cu bon de consum si AWB, fara sa
    treaca prin gestiunea cuiva. Alexandru, 30.09.2026: „se face direct bon de consum, cand se
    trimit direct catre colaborator pentru promovare". Pana acum drumul asta trecea prin WhatsApp.
  */
  const [tip, setTip] = useState<'activitate' | 'colaborator' | 'partener'>(
    initial?.tip ?? 'activitate',
  )
  const [influencerId, setInfluencerId] = useState<number | null>(initial?.influencer?.id ?? null)
  /*
    `partener` — marfa pleaca tot direct, cu bon de consum si AWB, dar primitorul e o PERSOANA
    JURIDICA. Alexandru, 01.10.2026: „la sponsorizare, destinatarul e firma partenera".

    Diferenta fata de colaborator nu e drumul marfii, ci ce scrie pe acte: contractul de
    sponsorizare si procesul-verbal cer denumire si cod fiscal, nu un cont de Instagram.
  */
  const [partener, setPartener] = useState<{ id: number; nume: string } | null>(
    initial?.client ? { id: initial.client.id, nume: initial.client.name } : null,
  )
  /*
    ADRESA STA PE CERERE, nu pe fisa partenerului.

    La influencer sta pe fisa lui: omul e acelasi, adresa nu se schimba, se completeaza o data. La
    o sponsorizare se schimba aproape de fiecare data — produsele ajung unde se tine evenimentul,
    nu la sediul fiscal al beneficiarului. Scrisa pe fisa, adresa de azi ar strica-o pe a
    urmatoarei livrari.
  */
  const [livrare, setLivrare] = useState<Livrare>(() => livrareDin(initial?.livrare))
  /*
    „Trimite prin curier" (16.10.2026): AWB după aprobare, cu datele de mai jos, la orice tip.
    Cererile noi către colaborator sau partener pornesc bifate — marfa lor pleacă oricum prin curier.
  */
  const [prinCurier, setPrinCurier] = useState<boolean>(initial?.prinCurier ?? false)
  const [arataErori, setArataErori] = useState<boolean>(!!initial?.prinCurier)
  function alegeTip(t: 'activitate' | 'colaborator' | 'partener'): void {
    setTip(t)
    if (!initial && t !== 'activitate') setPrinCurier(true)
  }
  const [puncte, setPuncte] = useState<Partener['puncte']>([])

  /**
   * Alegerea partenerului pre-completeaza adresa, dar numai daca e goala.
   *
   * Pre-completarea e o usurare, nu o decizie: cine a scris deja alta adresa — locul
   * evenimentului, nu sediul — n-are de ce s-o piarda fiindca a corectat partenerul.
   */
  function alegePartener(p: Partener | null): void {
    setPartener(p ? { id: p.id, nume: p.nume } : null)
    setPuncte(p?.puncte ?? [])
    if (!p) return
    setLivrare((l) =>
      l.judet || l.localitate || l.strada
        ? l
        : livrareDin({
            nume: p.nume,
            contact: p.persoanaContact,
            telefon: p.telefon,
            judet: p.judet,
            localitate: p.localitate,
            strada: p.adresa,
            codPostal: p.codPostal,
          }),
    )
  }

  /** Completarea din punctul de lucru ales. Suprascrie: omul tocmai a cerut asta. */
  function dinPunct(id: number): void {
    const b = puncte.find((x) => x.id === id)
    if (!b) return
    setLivrare((l) =>
      livrareDin({
        nume: partener?.nume ?? l.nume,
        contact: b.persoana,
        telefon: b.telefon,
        email: l.email,
        judet: b.judet,
        localitate: b.localitate,
        strada: b.adresa,
        codPostal: b.codPostal,
      }),
    )
  }
  /**
   * Adresa colaboratorului, din fișa lui. `doarDacaGoala`: la schimbarea colaboratorului, o adresă
   * deja scrisă (corectată de mână) nu se pierde; la bifarea „prin curier" se completează.
   */
  function dinInfluencer(id: number, doarDacaGoala: boolean): void {
    const i = influenceri.data?.find((x) => x.id === id)
    if (!i) return
    setLivrare((l) =>
      doarDacaGoala && (l.judet || l.strada)
        ? l
        : livrareDin({
            nume: i.numeDestinatar,
            contact: i.numeDestinatar,
            telefon: i.telefon,
            email: i.contactEmail,
            judet: i.judet,
            localitate: i.localitate,
            strada: i.strada,
            numar: i.numarStrada,
            codPostal: i.codPostal,
          }),
    )
  }
  const influenceri = useQuery({
    queryKey: ['influenceri-cerere'],
    enabled: tip === 'colaborator',
    queryFn: async () =>
      (
        await api.get<{
          influencers: {
            id: number
            handle: string
            platform: string
            numeDestinatar: string | null
            telefon: string | null
            contactEmail: string | null
            judet: string | null
            localitate: string | null
            strada: string | null
            numarStrada: string | null
            codPostal: string | null
          }[]
        }>('/marketing/influencers', { params: { pageSize: 200 } })
      ).data.influencers,
  })
  const [cauta, setCauta] = useState('')
  const [alese, setAlese] = useState<LinieAleasa[]>(
    (initial?.linii ?? []).map((l) => ({
      productId: l.productId,
      sku: l.sku,
      denumire: l.denumire,
      cantitate: l.cantitateCeruta,
    })),
  )

  const catalog = useQuery<{ produse: ProdusCatalog[] }>({
    queryKey: ['sampling', 'produse'],
    queryFn: async () => (await api.get('/marketing/sampling/produse')).data,
  })

  /*
    Acțiunile activității, pentru „Pentru care acțiune”.

    Alexandru, 29.09.2026: „la Necesar nu se înțelege pentru care acțiune au fost cerute
    produsele”. Textul liber de deasupra nu ține locul legăturii — două cereri de 20 de bucăți pe
    același program trimestrial arătau la fel.
  */
  const actiuni = useQuery<Actiune[]>({
    queryKey: ['sampling', 'actiuni', 'campanie', campanieId],
    queryFn: async () =>
      (await api.get(`/marketing/sampling/actiuni?campanieId=${campanieId}`)).data,
  })

  const aleseIds = new Set(alese.map((l) => l.productId))
  const termen = cauta.trim().toLowerCase()
  const disponibile = (catalog.data?.produse ?? [])
    .filter((p) => !aleseIds.has(p.id))
    .filter((p) => !termen || `${p.name} ${p.sku}`.toLowerCase().includes(termen))

  function adauga(p: ProdusCatalog): void {
    setAlese((l) => [...l, { productId: p.id, sku: p.sku, denumire: p.name, cantitate: 1 }])
    setCauta('')
  }

  const creata = useRef<number | null>(initial?.id ?? null)
  const salveaza = useMutation({
    mutationFn: async (siTrimite: boolean = false) => {
      const corp = {
        motiv: motiv.trim() || null,
        actiuneId,
        tip,
        influencerId: tip === 'colaborator' ? influencerId : null,
        clientId: tip === 'partener' ? (partener?.id ?? null) : null,
        livrare: tip === 'partener' || prinCurier ? livrare : null,
        prinCurier,
        linii: alese
          .filter((l) => l.cantitate > 0)
          .map((l) => ({ productId: l.productId, cantitate: l.cantitate })),
      }
      /*
        Cererea creată la un clic anterior se EDITEAZĂ, nu se creează din nou: „Trimite cererea”
        face două apeluri, iar dacă al doilea pică (ex. adresa incompletă), ciorna există deja —
        al doilea clic ar fi dublat-o.
      */
      const id = creata.current
      const r: { cerere: { id: number } } = id
        ? (await api.patch(`/marketing/necesar/cerere/${id}`, corp)).data
        : (await api.post(`/marketing/necesar/${campanieId}`, corp)).data
      creata.current = r.cerere.id
      if (siTrimite) await api.post(`/marketing/necesar/cerere/${r.cerere.id}/trimite`)
      return { siTrimite }
    },
    onSuccess: ({ siTrimite }) => {
      toast.success(siTrimite ? 'Cerere trimisă' : initial ? 'Cerere salvată' : 'Cerere creată')
      onGata()
      onClose()
    },
    onError: (e) => {
      // Ciorna poate exista deja (a picat doar trimiterea): lista trebuie s-o arate.
      if (creata.current) onGata()
      eroare(e)
    },
  })

  const valide = alese.filter((l) => l.cantitate > 0).length
  const lipsesteMotiv = teren && !motiv.trim()

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={initial ? 'Editează cererea' : 'Cerere de marfă'}
      inchideLaClicInAfara={false}
    >
      <div className="space-y-4">
        {/*
          Tipul, PRIMUL: schimbă tot ce urmează. La colaborator nu se mai cere acțiunea — marfa nu
          ajunge la nimeni din firmă — dar se cere destinatarul, fără de care nu există AWB.
        */}
        <div className={`flex flex-wrap gap-1 ${teren ? 'hidden' : ''}`}>
          {(
            [
              ['activitate', 'Pentru activitate'],
              ['colaborator', 'Pentru un colaborator'],
              ['partener', 'Pentru un partener'],
            ] as const
          )
            .filter(([cheie]) => cheie !== 'colaborator' || vedeColaboratori || tip === cheie)
            .map(([cheie, eticheta]) => (
              <button
                key={cheie}
                type="button"
                onClick={() => alegeTip(cheie)}
                className={`rounded border px-3 py-1 text-xs ${
                  tip === cheie
                    ? 'border-b4l-blue bg-b4l-blue-light/30 text-b4l-blue-dark'
                    : 'border-b4l-border bg-white hover:bg-b4l-bg'
                }`}
              >
                {eticheta}
              </button>
            ))}
        </div>

        {tip === 'colaborator' && (
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Cui îi trimitem
            <select
              value={influencerId ?? ''}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null
                setInfluencerId(id)
                if (id) dinInfluencer(id, true)
              }}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            >
              <option value="">— alege colaboratorul —</option>
              {(influenceri.data ?? []).map((i) => (
                <option key={i.id} value={i.id}>
                  {i.numeDestinatar ? `${i.numeDestinatar} · ` : ''}
                  {i.handle} ({i.platform})
                </option>
              ))}
            </select>
            <span className="text-xs text-b4l-text-dim">
              Adresa se completează din fișa lui (Marketing → Influenceri) și se poate corecta mai
              jos. Pe AWB pleacă cea de pe cerere.
            </span>
          </label>
        )}

        {tip === 'partener' && (
          <div className="space-y-3 rounded border border-b4l-border bg-b4l-bg-soft p-3">
            <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
              Cui îi trimitem
              <CautaAdaugaPartener
                baza="/marketing/necesar"
                value={partener}
                onChange={alegePartener}
                grupuri={GRUPURI_BENEFICIAR}
                grupImplicit="partener"
              />
              <span className="text-xs text-b4l-text-dim">
                Denumirea și codul fiscal ajung pe procesul-verbal de predare-primire. La
                sponsorizări, ele trebuie să fie cele din contract.
              </span>
            </label>

            {puncte.length > 0 && (
              <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
                Completează adresa din punctul de lucru
                <select
                  defaultValue=""
                  onChange={(e) => e.target.value && dinPunct(Number(e.target.value))}
                  className="rounded border border-b4l-border px-2 py-1.5 text-sm"
                >
                  <option value="">— sediul partenerului —</option>
                  {puncte.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nume}
                      {b.localitate ? ` · ${b.localitate}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        {tip === 'activitate' && !teren && (
          <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
            Pentru care acțiune
            <select
              value={actiuneId ?? ''}
              onChange={(e) => setActiuneId(e.target.value ? Number(e.target.value) : null)}
              className="rounded border border-b4l-border px-2 py-1.5 text-sm"
            >
              <option value="">— pentru activitate în general —</option>
              {(actiuni.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.denumire}
                </option>
              ))}
            </select>
            <span className="text-xs text-b4l-text-dim">
              Apare la Necesar, pe capul cererii. Lasă gol dacă ceri marfă înainte să existe
              acțiunile.
            </span>
          </label>
        )}

        {/*
          TRIMITE PRIN CURIER (16.10.2026): AWB după aprobare, la orice tip. Cu bifa, cererea nu
          se trimite fără date exacte — „nu vrem AWB-uri generate greșit" (Madalin). La partener
          adresa se arată mereu: e și adresa de pe procesul-verbal.
        */}
        <div className="space-y-3 rounded border border-b4l-border bg-b4l-bg-soft p-3">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={prinCurier}
              onChange={(e) => {
                setPrinCurier(e.target.checked)
                if (e.target.checked) {
                  setArataErori(true)
                  if (tip === 'colaborator' && influencerId) dinInfluencer(influencerId, false)
                }
              }}
            />
            <span>
              <span className="font-medium">Trimite prin curier</span>
              <span className="block text-xs text-b4l-text-muted">
                AWB la FAN Courier, după aprobare — niciodată înainte. Cererea se trimite doar cu
                datele de livrare complete și cu greutatea fiecărui produs în catalog.
              </span>
            </span>
          </label>
          {(prinCurier || tip === 'partener') && (
            <BlocLivrare
              value={livrare}
              onChange={setLivrare}
              cuFirma={tip === 'partener'}
              arataErori={prinCurier && arataErori}
            />
          )}
        </div>

        <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
          {teren ? (
            <span>
              Pentru ce și unde ajunge marfa <span className="text-b4l-red-dark">*</span>
            </span>
          ) : (
            'Pentru ce'
          )}
          <input
            value={motiv}
            onChange={(e) => setMotiv(e.target.value)}
            placeholder={
              teren
                ? 'Mostre pentru farmaciile Dr. Max și Catena din Ploiești'
                : '2 prezentări × 20 de persoane, 23 și 30 septembrie'
            }
            className="rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
        </label>

        <div className="rounded border border-b4l-border">
          <div className="flex items-center gap-2 border-b border-b4l-border bg-b4l-bg-soft px-3 py-2">
            <span className="text-sm font-medium">Ce ai cerut</span>
            <span className="text-xs text-b4l-text-muted">
              {alese.length === 0 ? 'nimic încă' : `${alese.length} produse`}
            </span>
          </div>
          {alese.length === 0 ? (
            <p className="px-3 py-3 text-sm text-b4l-text-muted">
              Caută mai jos și apasă „Adaugă”. Produsele alese apar aici, cu cantitățile lor.
            </p>
          ) : (
            <ul className="divide-y divide-b4l-border">
              {alese.map((l) => (
                <li key={l.productId} className="flex items-center gap-2 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {l.denumire} <span className="text-xs text-b4l-text-dim">{l.sku}</span>
                  </span>
                  <input
                    type="number"
                    min={1}
                    value={l.cantitate}
                    onChange={(e) =>
                      setAlese((x) =>
                        x.map((y) =>
                          y.productId === l.productId
                            ? { ...y, cantitate: Number(e.target.value || 0) }
                            : y,
                        ),
                      )
                    }
                    className="w-20 rounded border border-b4l-border px-2 py-1 text-right text-sm"
                  />
                  <button
                    onClick={() => setAlese((x) => x.filter((y) => y.productId !== l.productId))}
                    className="rounded p-1 text-b4l-text-muted hover:bg-b4l-bg hover:text-b4l-red-dark"
                    title="Scoate din cerere"
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <input
            value={cauta}
            onChange={(e) => setCauta(e.target.value)}
            placeholder="Caută produs după denumire sau cod…"
            className="mb-2 w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
          />
          <div className="max-h-56 overflow-auto rounded border border-b4l-border">
            {disponibile.length === 0 ? (
              <p className="p-3 text-sm text-b4l-text-muted">
                {catalog.isLoading
                  ? 'Se încarcă…'
                  : termen
                    ? 'Niciun produs găsit. Caută după denumire — codul din ExpertAccounts nu coincide întotdeauna cu cel din CRM.'
                    : 'Toate produsele sunt deja în cerere.'}
              </p>
            ) : (
              <ul className="divide-y divide-b4l-border">
                {disponibile.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {p.name} <span className="text-xs text-b4l-text-dim">{p.sku}</span>
                    </span>
                    <Button
                      variant="secondary"
                      className="!px-2 !py-1 !text-xs"
                      onClick={() => adauga(p)}
                    >
                      Adaugă
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-xs text-b4l-text-muted">
            {valide === 0 ? 'Nicio linie' : `${valide} linii de cerut`}
          </span>
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button
            variant={cuTrimitere ? 'secondary' : undefined}
            disabled={valide === 0 || lipsesteMotiv || salveaza.isPending}
            onClick={() => salveaza.mutate(false)}
          >
            {salveaza.isPending ? 'Se salvează…' : initial ? 'Salvează' : 'Salvează ciorna'}
          </Button>
          {cuTrimitere && (
            <Button
              disabled={valide === 0 || lipsesteMotiv || salveaza.isPending}
              onClick={() => salveaza.mutate(true)}
            >
              Trimite cererea
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
